import { supabase } from "@/integrations/supabase/client";
import { KnowledgeBaseDocument } from "@/types/supabase";
import { generateEmbedding, expandQueryWithLLM } from "./embeddings";

interface SearchOptions {
  threshold?: number;
  includeContent?: boolean;
  limit?: number;
  textQuery?: string; // For text-based search
  logDiagnostics?: boolean;
  useQueryExpansion?: boolean; // Whether to use LLM-powered query expansion
}

/**
 * Normalizes scientific terms and units to standardized forms
 * @param query The query string to normalize
 * @returns Normalized query string
 */
function normalizeScientificQuery(query: string): string {
  return query
    .toLowerCase()
    // Normalize units
    .replace(/μg\/l|ug\/l|micrograms per liter/gi, 'ug/l')
    .replace(/mg\/l|milligrams per liter/gi, 'mg/l')
    // Normalize chemical elements and compounds
    .replace(/\bcadmium\b/gi, 'cd')
    .replace(/\bzinc\b/gi, 'zn')
    .replace(/\bcopper\b/gi, 'cu')
    .replace(/\blead\b/gi, 'pb')
    .replace(/\bmercury\b/gi, 'hg')
    // Normalize species names
    .replace(/\banodonta\s+grandis\s+grandis\b/gi, 'anodonta grandis grandis')
    .replace(/\banodonta\s+grandis\b/gi, 'anodonta grandis')
    // Normalize biological terms
    .replace(/\bmetallothionein\b/gi, 'mt')
    .replace(/\bgill\s+tissue\b/gi, 'gill')
    .replace(/\bmantle\s+tissue\b/gi, 'mantle');
}

/**
 * Performs a search using Postgres vector search via RPC function
 * @param knowledgeBaseId Knowledge base ID to search in
 * @param queryEmbedding The query embedding vector
 * @param threshold Similarity threshold
 * @param limit Maximum number of results
 * @returns Array of matching documents
 */
async function performRPCVectorSearch(
  knowledgeBaseId: string,
  queryEmbedding: number[],
  threshold: number,
  limit: number,
  logDiagnostics = false
): Promise<KnowledgeBaseDocument[]> {
  try {
    if (logDiagnostics) {
      console.log(`[Search] Attempting RPC vector search with threshold ${threshold} and limit ${limit}`);
    }

    // Use a type assertion to address the TypeScript error with the RPC function name
    const { data, error } = await (supabase.rpc as any)("match_documents", {
      query_embedding: queryEmbedding,
      match_threshold: threshold,
      match_count: limit,
      knowledge_base_id: knowledgeBaseId
    });

    if (error) {
      console.error("Error in RPC vector search:", error);
      throw error;
    }

    if (logDiagnostics) {
      console.log(`[Search] RPC vector search found ${data?.length || 0} results`);
    }

    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    // Transform the results to match KnowledgeBaseDocument type
    const transformedResults: KnowledgeBaseDocument[] = data.map(item => ({
      id: String(item.id),
      knowledge_base_id: knowledgeBaseId,
      name: String(item.name || ''),
      content: String(item.content || ''),
      file_type: String(item.file_type || ''),
      size: Number(item.size || 0),
      created_at: String(item.created_at || ''),
      updated_at: String(item.updated_at || ''),
      similarity: Number(item.similarity || 0)
    }));

    return transformedResults;
  } catch (error) {
    console.error("Error in RPC vector search:", error);
    return []; // Return empty array on error
  }
}

/**
 * Performs a manual vector similarity search on the documents
 * @param knowledgeBaseId Knowledge base ID to search in
 * @param queryEmbedding The query embedding vector
 * @param threshold Similarity threshold
 * @param limit Maximum number of results
 * @returns Array of matching documents with similarity scores
 */
async function performManualVectorSearch(
  knowledgeBaseId: string,
  queryEmbedding: number[],
  threshold: number,
  limit: number,
  logDiagnostics = false
): Promise<KnowledgeBaseDocument[]> {
  try {
    if (logDiagnostics) {
      console.log(`[Search] Attempting manual vector search with threshold ${threshold} and limit ${limit}`);
    }

    const { data, error } = await supabase
      .from('knowledge_base_documents')
      .select('*')
      .eq('knowledge_base_id', knowledgeBaseId)
      .not('embedding', 'is', null);
    
    if (error) {
      console.error('Error fetching documents with embeddings:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      return [];
    }
    
    // Check if the first document has an embedding property
    const hasEmbedding = data[0] && 'embedding' in data[0] && data[0].embedding !== null;
    
    if (!hasEmbedding) {
      console.warn('Documents do not have embeddings');
      return [];
    }

    // Manually calculate cosine similarity for each document
    // Use a type assertion to tell TypeScript that these documents include embeddings
    type DocumentWithEmbedding = {
      id: string;
      knowledge_base_id: string;
      name: string;
      content: string;
      file_type: string;
      size: number;
      created_at: string;
      updated_at: string;
      embedding: number[];
    };

    const documentsWithSimilarity = (data as DocumentWithEmbedding[])
      .filter(doc => doc.embedding !== null)
      .map(doc => {
        // Calculate cosine similarity
        let dotProduct = 0;
        let docMagnitude = 0;
        let queryMagnitude = 0;
        
        for (let i = 0; i < queryEmbedding.length; i++) {
          if (i < doc.embedding.length) {
            dotProduct += queryEmbedding[i] * doc.embedding[i];
            docMagnitude += doc.embedding[i] * doc.embedding[i];
          }
          queryMagnitude += queryEmbedding[i] * queryEmbedding[i];
        }
        
        docMagnitude = Math.sqrt(docMagnitude);
        queryMagnitude = Math.sqrt(queryMagnitude);
        
        const similarity = dotProduct / (docMagnitude * queryMagnitude);
        
        return {
          ...doc,
          similarity
        } as KnowledgeBaseDocument;
      })
      .filter(doc => doc.similarity > threshold)
      .sort((a, b) => (b.similarity || 0) - (a.similarity || 0))
      .slice(0, limit);
      
    if (logDiagnostics) {
      console.log(`[Search] Manual vector search found ${documentsWithSimilarity.length} matches`);
    }
    
    return documentsWithSimilarity;
  } catch (error) {
    console.error("Error in manual vector search:", error);
    return []; // Return empty array on error
  }
}

/**
 * Performs a semantic search against document embeddings in the knowledge base
 * @param knowledgeBaseId The ID of the knowledge base to search
 * @param query The search query text
 * @param options Search configuration options or legacy limit parameter
 * @returns Array of matching documents sorted by similarity
 */
export async function semanticSearch(
  knowledgeBaseId: string,
  query: string,
  options: SearchOptions | number = {}
): Promise<KnowledgeBaseDocument[]> {
  // Convert numeric parameter to options object (for backward compatibility)
  if (typeof options === 'number') {
    options = { limit: options };
  }

  const {
    threshold = 0.5,
    includeContent = true,
    limit = 10,
    textQuery = "",
    logDiagnostics = false,
    useQueryExpansion = true, // Default to using query expansion for better results
  } = options as SearchOptions;

  // If query is empty, return early
  if (!query.trim() && !textQuery.trim()) {
    return [];
  }
  
  try {
    // Enhanced scientific term detection - expand pattern matching
    const scientificTerms = [
      'biomarker', 'biomarkers', 'mussel', 'mussels', 'metallothionein', 'mt', 
      'anodonta', 'grandis', 'anodonta grandis', 'anodonta grandis grandis',
      'cadmium', 'cd', 'exposure', 'concentration', 
      'mg/l', 'exposure concentration', 'tissues', 'gill', 'mantle', 'kidney',
      'μg/l', 'ug/l', 'metal', 'heavy metal', 'induction', 'bioaccumulation',
      'toxicity', 'table', 'figure', 'study', 'experiment'
    ];
    
    // Improved pattern matching for scientific queries
    const isScientificQuery = scientificTerms.some(term => 
      query.toLowerCase().includes(term.toLowerCase())
    ) || 
    /anodonta\s+grandis(\s+grandis)?/i.test(query) ||
    /metallothio(n|nein)/i.test(query) ||
    /\d+(\.\d+)?\s*(mg|μg|ug)\/l/i.test(query) || // Detect concentration expressions
    /\b[A-Z][a-z]?[0-9]*\b/.test(query); // Chemical element symbols
    
    // Use a much lower threshold for scientific/biomedical queries as they are more specialized
    const searchThreshold = isScientificQuery ? 0.01 : threshold;
    
    if (logDiagnostics) {
      console.log(`[Search] Query: "${query}", KB: ${knowledgeBaseId}`);
      console.log(`[Search] Using ${isScientificQuery ? 'scientific/biomedical' : 'standard'} threshold: ${searchThreshold}`);
      if (isScientificQuery) {
        console.log(`[Search] Detected scientific terms in query`);
      }
    }

    // Apply query transformations for scientific terms to improve matching
    let enhancedQuery = query;
    
    if (isScientificQuery) {
      // For scientific queries, create multiple enhanced versions to increase chance of matches
      if (/anodonta\s+grandis(\s+grandis)?/i.test(query)) {
        if (!/concentration|exposure|cadmium|cd/i.test(query)) {
          // Try to match with exposure studies
          enhancedQuery = `${query} exposure concentration cadmium metallothionein`;
          console.log(`Enhanced scientific query: "${enhancedQuery}"`);
        }
      }
      
      if (/cadmium|cd/i.test(query.toLowerCase()) && !/concentration|exposure/i.test(query.toLowerCase())) {
        // Try to match with concentration studies
        enhancedQuery = `${query} concentration exposure μg/l`;
        console.log(`Enhanced metal query: "${enhancedQuery}"`);
      }
    }
    
    // Apply scientific normalization before generating embedding
    const normalizedQuery = normalizeScientificQuery(enhancedQuery);
    if (logDiagnostics && normalizedQuery !== enhancedQuery) {
      console.log(`[Search] Normalized query: "${normalizedQuery}"`);
    }
    
    // NEW: LLM-based query expansion (only if enabled and for scientific queries)
    let allResults: KnowledgeBaseDocument[] = [];
    
    if (useQueryExpansion && isScientificQuery) {
      if (logDiagnostics) {
        console.log(`[Search] Using LLM-based query expansion`);
      }
      
      // Get expanded queries
      const expandedQueries = await expandQueryWithLLM(normalizedQuery);
      
      if (logDiagnostics) {
        console.log(`[Search] Expanded to ${expandedQueries.length} queries:`, expandedQueries);
      }
      
      // Process each expanded query
      for (const expandedQuery of expandedQueries) {
        const expandedEmbedding = await generateEmbedding(expandedQuery);
        
        if (!expandedEmbedding) {
          console.warn(`Could not generate embedding for expanded query: "${expandedQuery}"`);
          continue;
        }
        
        // Try RPC vector search first
        try {
          const rpcResults = await performRPCVectorSearch(
            knowledgeBaseId, 
            expandedEmbedding, 
            searchThreshold, 
            Math.ceil(limit / expandedQueries.length), // Distribute the limit among queries
            logDiagnostics
          );
          
          if (rpcResults.length > 0) {
            allResults.push(...rpcResults);
            continue; // Skip manual search if RPC worked
          }
        } catch (rpcError) {
          console.warn('RPC vector search failed, falling back to manual search:', rpcError);
          // Continue to manual search
        }
        
        // Fall back to manual vector search
        const manualResults = await performManualVectorSearch(
          knowledgeBaseId,
          expandedEmbedding,
          searchThreshold,
          Math.ceil(limit / expandedQueries.length), // Distribute the limit
          logDiagnostics
        );
        
        allResults.push(...manualResults);
      }
      
      // Deduplicate results based on document ID
      const seenIds = new Set<string>();
      allResults = allResults.filter(doc => {
        if (seenIds.has(doc.id)) {
          return false;
        }
        seenIds.add(doc.id);
        return true;
      });
      
      // Re-sort by similarity
      allResults.sort((a, b) => (b.similarity || 0) - (a.similarity || 0));
      
      // Apply final limit
      allResults = allResults.slice(0, limit);
      
      if (logDiagnostics) {
        console.log(`[Search] Combined results from query expansion: ${allResults.length} unique documents`);
      }
      
      // If we found results with expanded queries, return them
      if (allResults.length > 0) {
        return allResults;
      }
    }
    
    // If query expansion didn't yield results or wasn't used, continue with regular search
    // Generate embedding vector for the normalized query
    const queryEmbedding = await generateEmbedding(normalizedQuery);

    if (logDiagnostics) {
      console.log("[KB DEBUG] Normalized Query:", normalizedQuery);
      console.log("[KB DEBUG] Query Embedding:", queryEmbedding?.slice(0, 5));
      console.log("[KB DEBUG] Query Embedding Length:", queryEmbedding?.length);
      if (!queryEmbedding || queryEmbedding.length !== 1536) {
        console.warn("[KB DEBUG] ⚠️ Unexpected embedding size. Expected 1536.");
      }
    }

    if (!queryEmbedding) {
      console.warn('Could not generate embedding for query. Falling back to text search.');
      return performTextSearch(knowledgeBaseId, query, limit);
    }

    // Try RPC vector search first (more efficient)
    try {
      const rpcResults = await performRPCVectorSearch(
        knowledgeBaseId, 
        queryEmbedding, 
        searchThreshold, 
        limit,
        logDiagnostics
      );
      
      if (rpcResults.length > 0) {
        if (logDiagnostics) {
          console.log(`[Search] Using RPC vector search results: ${rpcResults.length} documents`);
        }
        return rpcResults;
      }
    } catch (rpcError) {
      console.warn('RPC vector search failed, falling back to manual search:', rpcError);
      // Continue to manual search
    }
    
    // Fall back to manual vector search
    const manualResults = await performManualVectorSearch(
      knowledgeBaseId,
      queryEmbedding,
      searchThreshold,
      limit,
      logDiagnostics
    );
    
    if (manualResults.length > 0) {
      if (logDiagnostics) {
        console.log(`[Search] Using manual vector search results: ${manualResults.length} documents`);
      }
      return manualResults;
    }
    
    // Fall back to text search as a last resort
    console.log('No vector search results, falling back to text search');
    return performTextSearch(knowledgeBaseId, query, limit);
    
  } catch (error) {
    console.error('Error in semantic search:', error);
    // Fall back to text search on error
    return performTextSearch(knowledgeBaseId, query, limit);
  }
}

/**
 * Perform a text-based search as fallback when vector search fails
 */
async function performTextSearch(knowledgeBaseId: string, query: string, limit: number): Promise<KnowledgeBaseDocument[]> {
  try {
    console.log(`Performing text search for: "${query}"`);
    
    // For highly specific scientific queries, first try an exact match with specific terms
    if (/anodonta\s+grandis(\s+grandis)?/i.test(query)) {
      // Try direct table lookup as a special case for this specific species
      try {
        const { data: directMatches, error: directMatchError } = await supabase
          .from('knowledge_base_documents')
          .select('id, knowledge_base_id, name, content, file_type, size, created_at, updated_at')
          .eq('knowledge_base_id', knowledgeBaseId)
          .textSearch('content', 'anodonta & grandis', {
            config: 'english',
            type: 'plain'
          })
          .limit(limit);
        
        if (directMatchError) {
          console.error('Error in direct text search:', directMatchError);
        } else if (directMatches && Array.isArray(directMatches) && directMatches.length > 0) {
          console.log(`Found ${directMatches.length} direct text matches for Anodonta grandis query`);
          
          // Create properly typed documents with high artificial similarity scores
          return directMatches.map(doc => ({
            id: String(doc.id || ''),
            knowledge_base_id: String(doc.knowledge_base_id || ''),
            name: String(doc.name || ''),
            content: String(doc.content || ''),
            file_type: String(doc.file_type || ''),
            size: Number(doc.size || 0),
            created_at: String(doc.created_at || ''),
            updated_at: String(doc.updated_at || ''),
            similarity: 0.95 // High artificial similarity for direct matches
          }));
        }
      } catch (directSearchError) {
        console.error('Error in direct text search:', directSearchError);
        // Continue with regular text search
      }
    }
    
    // Standard text search using websearch configuration
    const { data: documents, error: searchError } = await supabase
      .from('knowledge_base_documents')
      .select('id, knowledge_base_id, name, content, file_type, size, created_at, updated_at')
      .eq('knowledge_base_id', knowledgeBaseId)
      .textSearch('content', query, {
        config: 'english',
        type: 'websearch'
      })
      .limit(limit);

    if (searchError) {
      console.error('Error in text search:', searchError);
      return [];
    }

    // Process and return the results
    if (!documents || !Array.isArray(documents) || documents.length === 0) {
      console.log('No documents found with text search');
      return [];
    }

    // Create properly typed documents with artificial similarity scores
    const resultsWithSimilarity: KnowledgeBaseDocument[] = documents.map((doc, index) => ({
      id: String(doc.id || ''),
      knowledge_base_id: String(doc.knowledge_base_id || ''),
      name: String(doc.name || ''),
      content: String(doc.content || ''),
      file_type: String(doc.file_type || ''),
      size: Number(doc.size || 0),
      created_at: String(doc.created_at || ''),
      updated_at: String(doc.updated_at || ''),
      similarity: 0.9 - (index * 0.05) // Descending similarity scores
    }));
    
    console.log(`Found ${resultsWithSimilarity.length} results with text search`);
    
    return resultsWithSimilarity;
  } catch (textSearchError) {
    console.error('Error in text search approach:', textSearchError);
    return [];
  }
}
